import { hasRole } from './auth/AuthProvider';
import {
  NAV_ITEMS,
  SETTINGS_GROUPS,
  type NavItem,
  type NavSection,
  type SettingsGroup,
} from './nav';
import type { FeatureKey } from '@oip/contracts';

/**
 * The single decision point for navigation visibility.
 *
 * Visibility is a display concern only — the server is the authorization
 * boundary. Route guards deny access independently, so hiding an item never
 * substitutes for a guard.
 */
export interface NavAuth {
  role: string | null;
  isPlatformAdmin: boolean;
  /** Features enabled for the active tenant. */
  features?: readonly FeatureKey[];
  /**
   * True when a platform administrator has entered a tenant they are not a
   * member of. Without it they hold NO tenant context, and every
   * tenant-scoped route would fail with "Platform admin must specify
   * X-Tenant-Id" — so those links must not be offered.
   */
  hasAssumedTenant?: boolean;
  /**
   * True when the session administers a white-label partner.
   *
   * Derived from `me.whiteLabels`, which step 6 already returns. It is a
   * separate input from `role` and `isPlatformAdmin` because partner authority
   * is a third thing: a partner administrator holds no tenant membership and no
   * platform capability, so both of the existing gates would hide their own
   * console from them.
   */
  administersWhiteLabel?: boolean;
  /**
   * True when that authority is scoped to ONE RESELLER rather than the whole partner.
   *
   * ⚠️ Every partner item carries `whiteLabel: true`, so without this a reseller
   * administrator is offered Brand, Regional defaults, People and Resellers — and all four
   * 403, because the server denies the partner surface by default to a reseller. **A menu
   * listing screens you may not open is not a menu**, which is the reason this function
   * exists at all.
   *
   * Sent by the server on `me.whiteLabels[].resellerId` rather than inferred, because the
   * client cannot apply a rule whose input it was never given.
   */
  resellerScoped?: boolean;
  /**
   * Whether this session may only READ in the partner console.
   *
   * ⚠️ Optional and read with `=== true`, never `!== false`: a consumer built before this
   * field yields `undefined`, and treating that as read-only would hide a working page from
   * every administrator. The `resellerScoped` truthiness trap beside it, which already bit
   * once.
   */
  partnerReadOnly?: boolean;
}

/**
 * The partner-surface pages a RESELLER administrator may open.
 *
 * ⚠️ Mirrors `@ResellerScoped()` on the server, and the two must move together. Brand,
 * Regional defaults and People are the PARTNER's product identity and its own staff — a
 * reseller trades under that identity and does not set it. Resellers is the control that
 * decides what a reseller may sell, so offering it to one would make the restriction a
 * formality.
 */
/*
 * ⚠️ **This must MIRROR `@ResellerScoped()` on the server**, and the direction of a drift is
 * what makes it safe: the server is deny-by-default, so a path missing here hides a WORKING
 * page, while a path listed here that the server refuses would offer a screen that 403s.
 * Hiding is the survivable direction, which is why this is a declared list rather than a flag
 * on each item.
 *
 * `/partner/imports` is open to a reseller because the server fixes `partner_code` — and
 * `reseller_name` too, for a reseller-scoped caller — from the SESSION (D8/D15), so a file
 * naming a sibling's reseller has that column dropped rather than honoured.
 */
const RESELLER_PARTNER_PATHS = ['/partner/companies', '/partner/imports'];

/**
 * Partner pages a VIEWER has no use for, because they hold no readable content at all.
 *
 * Only the import: it is a file upload whose two actions are both refused to a viewer, so
 * the page would render a control that can never complete. Every other partner page is a
 * screen with something on it.
 */
const WRITE_ONLY_PARTNER_PATHS = ['/partner/imports'];

/**
 * Whether one navigation item should be offered to this session.
 *
 * The single answer, so nothing has to re-derive it. The command palette used
 * to, and honoured only `roles` — which meant every tenant user was offered the
 * whole PLATFORM section by name, and every feature-gated page regardless of
 * entitlement. The routes refused them, so nothing leaked, but a menu that
 * lists screens you may not open is not a menu, and it advertised the platform
 * surface to every customer.
 *
 * PRESENTATION ONLY. `RequirePlatform`, `RequireRole` and the server decide.
 */
export function isNavItemVisible(n: NavItem, auth: NavAuth): boolean {
  if (n.available === false) return false;
  if (n.platform) return auth.isPlatformAdmin;
  /*
   * Partner items, checked BEFORE the tenant-context branch below. A partner
   * administrator with no companies yet has no tenant context at all, so
   * falling through would hide the one screen that lets them create the first
   * company — the client-side shape of the same problem `WhiteLabelAuthorized`
   * solves on the server.
   *
   * A platform administrator is deliberately NOT included: platform authority
   * is global and does not make somebody an administrator OF a partner, and
   * the route refuses them.
   */
  if (n.whiteLabel) {
    if (auth.administersWhiteLabel !== true) return false;
    /*
     * ⚠️ A reseller sees only what it may actually open. The list is DECLARED here rather
     * than flagged per item, and deliberately so: the partner surface is deny-by-default on
     * the SERVER, so a new item is refused there whether or not anybody remembers this —
     * and an allow-list here that fell out of step hides a working page rather than
     * offering a broken one, which is the safe direction for the two to disagree in.
     */
    /*
     * ⚠️ **A VIEWER IS NOT OFFERED A PURE WRITE SURFACE.** Every other partner page has
     * something to read, so a viewer opens it and simply finds no controls — but
     * `/partner/imports` is a file upload whose only two actions (`validate` and `apply`)
     * are POSTs the guard refuses by verb. Listing it is precisely the defect
     * `isNavItemVisible` exists to prevent: a menu entry whose page can do nothing.
     *
     * Declared as a list of WRITE-ONLY paths rather than a flag per item, matching
     * `RESELLER_PARTNER_PATHS` above — and for the same reason. The server refuses these
     * whatever this says, so a path missing here hides nothing and a path wrongly listed
     * hides a page a viewer could not have used anyway.
     */
    if (auth.partnerReadOnly === true && WRITE_ONLY_PARTNER_PATHS.includes(n.path)) return false;
    return auth.resellerScoped !== true || RESELLER_PARTNER_PATHS.includes(n.path);
  }

  // An entitlement the tenant does not hold hides the item. A platform
  // administrator sees no more than the tenant does here — the point is to
  // reflect what this tenant bought, not who is looking.
  if (n.feature && !(auth.features ?? []).includes(n.feature)) return false;

  // Everything below is tenant-scoped and needs tenant context to work at
  // all. A platform administrator has it only after entering a tenant.
  //
  // `platformOwned` is the exception: the data behind it has no tenant, so a
  // platform administrator can use the page with no tenant context at all.
  // Without this the prompt registry was reachable only by typing the URL —
  // for precisely the role allowed to author a prompt.
  const hasTenantContext = auth.role !== null || auth.hasAssumedTenant === true;
  if (!hasTenantContext) return n.platformOwned === true && auth.isPlatformAdmin;

  // A platform admin who HAS entered a tenant satisfies any tenant role
  // requirement, because RolesGuard grants the same server-side.
  if (n.roles) return auth.isPlatformAdmin || hasRole(auth.role, ...n.roles);
  return true;
}

/**
 * What the SIDEBAR renders for a section.
 *
 * ⚠️ **An item with a `settingsGroup` is excluded here and NOWHERE ELSE.** That one line is the
 * whole navigation redesign: seventeen administration pages leave the daily menu and stay in
 * `NAV_ITEMS`, so `Breadcrumbs` still resolves their first-segment label, the command palette still
 * offers them by name, and `PlatformHome` still builds its card from them. Filtering anywhere
 * further up — by removing them from the list — would take all three with it, and the breadcrumb
 * loss is silent: the trail falls back to `titleCase('providers')` and looks merely ugly.
 */
export function visibleNavItems(section: NavSection, auth: NavAuth): NavItem[] {
  return NAV_ITEMS.filter(
    (n) => n.section === section && n.settingsGroup === undefined && isNavItemVisible(n, auth),
  );
}

/**
 * What the SETTINGS AREA renders, in declared group order.
 *
 * ⚠️ Gated by exactly the same `isNavItemVisible` — all three gates — so a page a reader may not
 * open is absent from Settings for the same reason it was absent from the sidebar. A second
 * visibility rule here is how the two would come to disagree, which is the defect this codebase has
 * recorded five times.
 */
export function settingsNavItems(auth: NavAuth): Array<[SettingsGroup, NavItem[]]> {
  const groups: Array<[SettingsGroup, NavItem[]]> = SETTINGS_GROUPS.map((group) => [
    group,
    NAV_ITEMS.filter((n) => n.settingsGroup === group && isNavItemVisible(n, auth)),
  ]);
  /*
   * ⚠️ A group with nothing visible is DROPPED rather than rendered empty — a heading over nothing
   * is the fault the tachograph card already records. `AI & automation` disappears entirely for an
   * operator, which is correct: every page in it is TENANT_ADMIN-only.
   */
  return groups.filter(([, items]) => items.length > 0);
}
